import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useMatch, useNavigate, useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { renderGeneAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";

import { useLoadDispatchWithIdAsNumber } from "../../app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { type IGeneState, shouldLoadGene } from "../../redux/modules/gene.js";
import { type IAppState } from "../../redux/reducer.js";
import DatasetViewer, { type IDatasetViewerOwnProps } from "../analytical-viewer/DatasetViewer.js";
import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { AnalyticalDataButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";

const GeneExpressionView: FC = () => {
  const navigate = useNavigate();
  const { geneId } = useParams<"geneId">();
  const match = useMatch("/gene/:geneId/expression/:datasetId");
  const { gene, isLoading } = useSelector<IAppState, IGeneState>((state) => state.gene);
  const viewId = useURLViewID();
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGene, geneId);

  useEffect(() => {
    if (!viewId) {
      void load();
    }
  }, [load, viewId]);

  let datasetViewerProps: IDatasetViewerOwnProps = {
    handleDatasetChanged: (datasetId: number) => {
      navigate(`/gene/${geneId}/expression/${datasetId}`);
    },
    datasetId: toSafeInt(match?.params.datasetId),
  };

  if (!viewId && !isLoading && gene) {
    datasetViewerProps = {
      ...datasetViewerProps,
      genomeId: gene.genome.id,
      defaultDataSeries: [gene.name],
    };
  } else if (viewId) {
    // Bootstrap from saved view
    datasetViewerProps = {
      ...datasetViewerProps,
      viewId,
    };
  }

  return (
    <>
      <PageHeader
        title={gene?.name}
        subtitle={gene?.description || "Gene Expression Data"}
        actionWidgets={[<AnalyticalDataButtonHeader key={"gene-expression-view-action-1"} />]}
        widgets={[
          <AttributesListWidget attributes={renderGeneAttributesWidgets(gene)} key={"gene-expression-view-widget-1"} />,
        ]}
      />

      <div className={styleLayout.page}>
        <DatasetViewer {...datasetViewerProps} />
      </div>
    </>
  );
};

export default GeneExpressionView;
